// src/components/charts/production-management-charts/ProductionJobsProgress.jsx
//
// One row per launched product, showing how far it has got.
//
// It used to be vertical bars — a column per job, labels rotated 25°. That reads
// well for eight products and falls apart at thirty: the labels overlap into a
// grey smear, the bars thin to hairlines, and on a phone there is no width left
// to give any of them. A product name is a long horizontal word, so it belongs
// on a horizontal axis; turning the chart on its side gives every job a full
// line of its own and makes the count a matter of height, which scrolls
// naturally, rather than width, which does not.
//
// The card stays a fixed height and shows VISIBLE_ROWS at a time; the rest are
// reached by dragging or scrolling the chart.
import React, { useMemo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import {
  TooltipComponent,
  GridComponent,
  DataZoomComponent
} from 'echarts/components';
import { BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../providers/AppProvider';

echarts.use([
  TooltipComponent,
  GridComponent,
  DataZoomComponent,
  BarChart,
  CanvasRenderer
]);

/** How many jobs fit in the card before it starts scrolling. */
const VISIBLE_ROWS = 9;

/**
 * Colour carries the state, not just decoration: at a glance you can see how
 * much of the floor is nearly done and how much has barely started. Ordered
 * grey → blue → green, so the eye reads progress as it moves down the scale.
 */
const PROGRESS_BANDS = {
  light: [
    { upTo: 24, color: '#94A3B8' }, // slate-400 — barely started
    { upTo: 59, color: '#38BDF8' }, // sky-400   — under way
    { upTo: 99, color: '#2563EB' }, // blue-600  — nearly there
    { upTo: 100, color: '#16A34A' } // green-600 — finished
  ],
  dark: [
    { upTo: 24, color: '#64748B' },
    { upTo: 59, color: '#22D3EE' },
    { upTo: 99, color: '#60A5FA' },
    { upTo: 100, color: '#4ADE80' }
  ]
};

const AXIS = {
  light: { text: '#0F172A', line: '#CBD5E1', track: '#E5EEFF' },
  dark: { text: '#E5E7EB', line: '#334155', track: '#1E293B' }
};

function clamp01(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(1, x));
}

// job.progress may be 0..1 or 0..100. Normalise to 0..100.
function jobProgressPercent(job) {
  const p = Number(job?.progress ?? 0);
  if (!Number.isFinite(p)) return 0;
  if (p <= 1) return Math.round(clamp01(p) * 100);
  return Math.round(Math.max(0, Math.min(100, p)));
}

function formatLabel(job) {
  return job?.productLabel || job?.jobCode || job?.name || 'Job';
}

const bandColor = (percent, isDark) => {
  const bands = PROGRESS_BANDS[isDark ? 'dark' : 'light'];
  return (bands.find(b => percent <= b.upTo) || bands[bands.length - 1]).color;
};

const getDefaultOptions = (isDark, jobs) => {
  const axis = AXIS[isDark ? 'dark' : 'light'];

  // Sorted so the chart says something on its own: what is about to finish, and
  // free a machine, sits at the top.
  const rows = jobs
    .map(job => ({ label: formatLabel(job), percent: jobProgressPercent(job) }))
    .sort((a, b) => b.percent - a.percent);

  const scrolls = rows.length > VISIBLE_ROWS;

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: params => {
        const p = params?.[0];
        if (!p) return '';
        // the axis label is truncated; the tooltip is where the full name lives
        //
        // `p.value`, not `p.data`: each bar is fed `{ value, itemStyle }` so it
        // can carry its own band colour, and `p.data` is that whole object —
        // which read as "[object Object]% completed". The bar's own label was
        // right the whole time because ECharts resolves `{c}` to the value.
        const percent = rows[p.dataIndex]?.percent ?? p.value;
        return `<div style="font-weight:700">${rows[p.dataIndex]?.label ?? p.axisValue}</div>
                <div>${percent}% completed</div>`;
      }
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLabel: { color: axis.text, formatter: v => `${v}%` },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: axis.line, type: 'dashed' } }
    },
    yAxis: {
      type: 'category',
      data: rows.map(r => r.label),
      // ECharts stacks the first category at the bottom; the most advanced job
      // should be the first thing read, so the axis is flipped instead.
      inverse: true,
      axisLabel: {
        color: axis.text,
        fontWeight: 600,
        formatter: value => {
          const s = String(value || '');
          return s.length > 18 ? `${s.slice(0, 18)}…` : s;
        }
      },
      axisLine: { lineStyle: { color: axis.line } },
      axisTick: { show: false }
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 16,
        showBackground: true,
        backgroundStyle: { color: axis.track, borderRadius: 4 },
        label: {
          show: true,
          position: 'right',
          formatter: '{c}%',
          color: axis.text,
          fontWeight: 600,
          fontSize: 11
        },
        itemStyle: { borderRadius: [0, 4, 4, 0] },
        data: rows.map(r => ({
          value: r.percent,
          itemStyle: { color: bandColor(r.percent, isDark) }
        }))
      }
    ],
    ...(scrolls
      ? {
          dataZoom: [
            { type: 'inside', yAxisIndex: 0, startValue: 0, endValue: VISIBLE_ROWS - 1 },
            {
              type: 'slider',
              yAxisIndex: 0,
              startValue: 0,
              endValue: VISIBLE_ROWS - 1,
              width: 8,
              right: 4,
              showDetail: false,
              brushSelect: false,
              handleSize: 0,
              borderColor: 'transparent',
              fillerColor: axis.line
            }
          ]
        }
      : {}),
    grid: {
      left: 4,
      // room for the "100%" label at the end of a full bar, and the scrollbar
      right: scrolls ? 46 : 34,
      bottom: 4,
      top: 8,
      containLabel: true
    },
    animation: false
  };
};

const ProductionJobsProgress = ({ jobs = [], style }) => {
  const {
    config: { theme }
  } = useAppContext();

  const safeJobs = Array.isArray(jobs) ? jobs : [];
  const isDark = theme === 'dark';
  const option = useMemo(
    () => getDefaultOptions(isDark, safeJobs),
    [isDark, safeJobs]
  );

  if (!safeJobs.length) {
    return (
      <div
        style={{
          height: style?.height || 360,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        className="text-700 fw-semibold"
      >
        No jobs to display
      </div>
    );
  }

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      style={style || { height: 360, width: '100%' }}
    />
  );
};

export default ProductionJobsProgress;
